import { RadioField, SelectField, TextField } from '@/components/forms/inputs';
import { ACCOUNT_PHRASES, ACCOUNT_TAX_DETAILS_PHRASES } from '@/consts/phrases/accountPhrases';
import {
    useCompletionStatus,
    usePhrase,
    useQueryParams,
    useServerErrors,
    useStatesQuery,
} from '@/hooks';
import { accountService } from '@/services/api';
import { SaveButtonWithCompletionStatus } from '@/components/general/buttons/SaveButton/SaveButtonWithCompletionStatus';
import { IAccountTaxDetails, TaxType } from '@/types';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useRouter } from '@/hooks';
import { InfoIcon } from '@/components/blocks/account/taxDetails/InfoIcon';
import { useSWRConfig } from 'swr';
import { config } from '@/config';
import { createSelectOptionsFromArray } from '@/utils/forms';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { EditableTextField } from '@/components/forms/inputs/EditableTextField/EditableTextField';
import { useState } from 'react';

const ACCOUNT_URL = config.api.url.account;
interface Props {
    accountData?: IAccountTaxDetails;
    onSuccess: () => void;
    taxDetails: IAccountTaxDetails;
}

export const AccountTaxDetailsForm = (props: Props) => {
    const { t } = usePhrase();
    const router = useRouter();
    const { hubinKey } = useQueryParams();
    const { mutate } = useSWRConfig();
    const [isEditingTin, setIsEditingTin] = useState(false);

    const { TIN, ...defaultValues } = props.taxDetails;

    const states = useStatesQuery('USA');
    const getStateSelectOptions = () =>
        createSelectOptionsFromArray({ label: 'name', value: 'stateCode' })(states || []);
    const stateSelectOptions = getStateSelectOptions();

    const {
        control,
        handleSubmit,
        formState,
        setError,
        setValue: setFormVal,
        getValues,
        clearErrors,
        watch,
    } = useForm<Partial<IAccountTaxDetails>>({
        defaultValues: { ...defaultValues, TIN: TIN || undefined },
    });

    const typeFormVal = watch('type');
    const typeFormValHasChanged = typeFormVal !== defaultValues.type;

    const { getCompletionStatus, resetCompletionStatus, completionStatus } = useCompletionStatus();
    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });
    const { errors, isSubmitting } = formState;

    const onSubmit = async (data: Partial<IAccountTaxDetails>) => {
        const TINValue = data?.TIN?.includes('*') ? undefined : data.TIN;
        try {
            const response = await getCompletionStatus(() =>
                accountService.saveAccountTaxDetails(
                    {
                        ...data,
                        lastName: typeFormVal === TaxType.EIN ? null : data.lastName,
                        TIN: TINValue,
                    },
                    hubinKey,
                ),
            );
            if (response.surveyUrl) {
                router.push(response.surveyUrl);
            }
            await mutate(ACCOUNT_URL + '/tax-details');
            props.onSuccess();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    const handleOnTypeChange = () => {
        const initialTinVal = TIN;
        if (typeFormValHasChanged && (isEditingTin || !initialTinVal)) {
            const NewTinVal = watch('TIN');
            if (NewTinVal) {
                setFormVal('TIN', '');
            }
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ mb: 4 }}
                    generalErrorId="ErrorAlert"
                />
            </Grid>
            <Paper className="accountTaxDetails" elevation={1} sx={{ p: 3 }}>
                <Grid container>
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 2,
                        }}
                    >
                        {t(ACCOUNT_TAX_DETAILS_PHRASES.SUBMITTING_VIA_WEB_FORM)}
                    </Typography>
                    <Grid size={12} sx={{ mb: 3 }}>
                        <Typography
                            variant="greyLabel"
                            sx={{ color: 'rgba(0, 0, 0, 0.7)', marginRight: 0.3 }}
                        >
                            {t(ACCOUNT_TAX_DETAILS_PHRASES.SELECT_ONE)}
                        </Typography>
                        <Typography variant="greyLabel" color="error">
                            *
                        </Typography>
                        <RadioField
                            control={control}
                            name="type"
                            data={Object.keys(TaxType).map((taxType) => ({
                                label: t(
                                    ACCOUNT_TAX_DETAILS_PHRASES[
                                        TaxType[taxType as keyof typeof TaxType]
                                    ],
                                ) as string,
                                value: taxType,
                            }))}
                            onChange={handleOnTypeChange}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Stack spacing={3}>
                            <Grid>
                                <EditableTextField
                                    control={control}
                                    name="TIN"
                                    label={t(ACCOUNT_TAX_DETAILS_PHRASES.TAX_PAYER_ID, {
                                        asString: true,
                                    })}
                                    initialValue={TIN || undefined}
                                    required={typeFormValHasChanged}
                                    error={errors.TIN}
                                    setValue={setFormVal}
                                    onIsEditingChange={setIsEditingTin}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 9,
                                                pattern: '\\d*',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <TextField
                                control={control}
                                label={t(ACCOUNT_TAX_DETAILS_PHRASES.FULL_NAME, { asString: true })}
                                name="fullName"
                                error={errors.fullName}
                                required
                            />
                            {typeFormVal !== TaxType.EIN && (
                                <TextField
                                    control={control}
                                    name="lastName"
                                    label={t(ACCOUNT_TAX_DETAILS_PHRASES.LAST_NAME, {
                                        asString: true,
                                    })}
                                    error={errors.lastName}
                                    required
                                />
                            )}
                            <TextField
                                control={control}
                                name="address"
                                label={t(ACCOUNT_TAX_DETAILS_PHRASES.ADDRESS, { asString: true })}
                                error={errors.address}
                                required
                            />
                            <TextField
                                control={control}
                                name="city"
                                label={t(ACCOUNT_TAX_DETAILS_PHRASES.CITY, { asString: true })}
                                error={errors.city}
                                required
                            />
                            <SelectField
                                control={control}
                                name="stateCode"
                                label={t(ACCOUNT_TAX_DETAILS_PHRASES.STATE, { asString: true })}
                                data={stateSelectOptions}
                                error={errors.stateCode}
                                required
                            />
                            <TextField
                                control={control}
                                name="zip"
                                label={t(ACCOUNT_TAX_DETAILS_PHRASES.ZIP, { asString: true })}
                                error={errors.zip}
                                required
                            />
                        </Stack>
                        <Grid
                            container
                            direction="row"
                            sx={{
                                justifyContent: 'flex-end',
                                my: 2,
                            }}
                        >
                            <SaveButtonWithCompletionStatus
                                id="tax-details-submit"
                                isSubmitting={isSubmitting}
                                status={completionStatus}
                                resetStatus={resetCompletionStatus}
                                label={ACCOUNT_PHRASES.SAVE}
                            />
                        </Grid>
                        <Box>
                            <Typography variant="body2">
                                {t(ACCOUNT_TAX_DETAILS_PHRASES.BY_SUBMITTING_THIS_FORM)}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box className="helperText">
                            <Grid
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <Box sx={{ height: 20, width: 20, mr: 0.5, mt: 0.25 }}>
                                    <InfoIcon />
                                </Box>
                                <Typography
                                    variant={'h5'}
                                    sx={(theme) => ({
                                        color: theme.colors.palette.secondary.main,
                                        mb: 2,
                                    })}
                                >
                                    {t(ACCOUNT_TAX_DETAILS_PHRASES.TAX_PAYER_ID_NUM)}
                                </Typography>
                            </Grid>
                            <Typography variant={'subtitle3'}>
                                {t(ACCOUNT_TAX_DETAILS_PHRASES.ENTER_TAX_PAYER_ID)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </form>
    );
};
